/**
 * Speech Provider (Penyedia Suara / Audio & TTS Ayo Baca)
 * 
 * Hybrid Architecture:
 * 1. Primary: High-fidelity native Indonesian TTS stream via `/api/tts?text=...`
 *    (Plays crisp human speech audio across ALL browsers and iframe sandboxes)
 * 2. Fallback: Browser Web Speech API (`SpeechSynthesis`) with auto voice matching
 * 3. Web Audio API synthesizer for immediate tactile chimes & sound effects
 */

import type { ChimeType, SpeechAudioProvider, SpeechOptions } from './speechContract';
import { shouldFallbackToOfflineSpeech } from './speechContract';

export class SpeechProvider implements SpeechAudioProvider {
  private currentAudio: HTMLAudioElement | null = null;
  private synth: SpeechSynthesis | null = null;
  private indonesianVoice: SpeechSynthesisVoice | null = null;
  private audioCtx: AudioContext | null = null;
  private activeUtterance: SpeechSynthesisUtterance | null = null;
  private audioCache = new Map<string, string>();
  private activeChimeOscillators = new Set<OscillatorNode>();
  private activeAudioCancel: (() => void) | null = null;
  private speechRequestId = 0;
  private activeSpeechText: string | null = null;
  private activeSpeechPromise: Promise<void> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      if ('speechSynthesis' in window) {
        this.synth = window.speechSynthesis;
        this.loadVoices();
        if (this.synth.onvoiceschanged !== undefined) {
          this.synth.onvoiceschanged = () => this.loadVoices();
        }
      }
    }
  }

  private loadVoices() {
    if (!this.synth) return;
    try {
      const voices = this.synth.getVoices();
      if (!voices || voices.length === 0) return;

      this.indonesianVoice =
        voices.find(v => v.lang === 'id-ID' || v.lang === 'id_ID' || v.lang.startsWith('id')) ||
        voices.find(v => v.name.toLowerCase().includes('indonesia')) ||
        null;
    } catch {
      // ignore
    }
  }

  /**
   * Initializes or resumes AudioContext on user interaction
   */
  public unlockAudio() {
    try {
      if (typeof window !== 'undefined' && !this.audioCtx) {
        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (AudioCtx) {
          this.audioCtx = new AudioCtx();
        }
      }
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
      if (this.synth && this.synth.paused) {
        this.synth.resume();
      }
    } catch (e) {
      console.warn('Audio unlock warning:', e);
    }
  }

  /**
   * Plays a pleasant melodic tone / chime using Web Audio API
   */
  public playChime(type: ChimeType = 'tap') {
    try {
      this.stopChime();
      this.unlockAudio();
      if (!this.audioCtx) return;

      const ctx = this.audioCtx;
      const now = ctx.currentTime;

      if (type === 'tap') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, now); // C5
        osc.frequency.exponentialRampToValueAtTime(659.25, now + 0.08); // E5
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
        osc.connect(gain);
        gain.connect(ctx.destination);
        this.activeChimeOscillators.add(osc);
        osc.onended = () => this.activeChimeOscillators.delete(osc);
        osc.start(now);
        osc.stop(now + 0.12);
      } else if (type === 'success') {
        const notes = [523.25, 659.25, 783.99, 1046.5];
        notes.forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, now + idx * 0.08);
          gain.gain.setValueAtTime(0.25, now + idx * 0.08);
          gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.08 + 0.25);
          osc.connect(gain);
          gain.connect(ctx.destination);
          this.activeChimeOscillators.add(osc);
          osc.onended = () => this.activeChimeOscillators.delete(osc);
          osc.start(now + idx * 0.08);
          osc.stop(now + idx * 0.08 + 0.25);
        });
      } else if (type === 'water') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(1400, now + 0.15);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.connect(gain);
        gain.connect(ctx.destination);
        this.activeChimeOscillators.add(osc);
        osc.onended = () => this.activeChimeOscillators.delete(osc);
        osc.start(now);
        osc.stop(now + 0.2);
      } else if (type === 'celebrate') {
        const notes = [440, 554.37, 659.25, 880, 1108.73];
        notes.forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, now + idx * 0.09);
          gain.gain.setValueAtTime(0.25, now + idx * 0.09);
          gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.09 + 0.35);
          osc.connect(gain);
          gain.connect(ctx.destination);
          this.activeChimeOscillators.add(osc);
          osc.onended = () => this.activeChimeOscillators.delete(osc);
          osc.start(now + idx * 0.09);
          osc.stop(now + idx * 0.09 + 0.35);
        });
      }
    } catch (e) {
      console.warn('Play chime error:', e);
    }
  }

  private stopChime() {
    for (const oscillator of this.activeChimeOscillators) {
      try {
        oscillator.stop();
      } catch {
        // Oscillator may already have ended.
      }
    }
    this.activeChimeOscillators.clear();
  }

  private stopActiveSpeech() {
    this.activeAudioCancel?.();
    this.activeAudioCancel = null;

    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
      } catch {
        // ignore
      }
      this.currentAudio = null;
    }
    this.synth?.cancel();
    this.activeUtterance = null;
  }

  /**
   * Primary: Plays authentic Indonesian speech via streaming audio endpoint
   */
  private async playAudioTTS(text: string): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    try {
      this.unlockAudio();

      this.stopActiveSpeech();

      const cleanText = text.trim();
      if (!cleanText) return false;

      const audioUrl = `/api/tts?text=${encodeURIComponent(cleanText)}`;
      console.log('[SpeechDebug] TTS audio start', { text: cleanText, time: new Date().toISOString() });
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;

      return new Promise<boolean>((resolve) => {
        let completed = false;
        let playbackStarted = false;
        const finish = (ok: boolean) => {
          if (!completed) {
            completed = true;
            console.log('[SpeechDebug] TTS audio finish', {
              text: cleanText,
              ok,
              event: ok ? 'ended' : 'error-or-cancel',
              time: new Date().toISOString(),
            });
            if (!ok) {
              try {
                audio.pause();
                audio.currentTime = 0;
              } catch {
                // ignore cleanup failures from an already-failed audio element
              }
            }
            if (this.activeAudioCancel === cancelAudio) this.activeAudioCancel = null;
            if (this.currentAudio === audio) this.currentAudio = null;
            resolve(ok);
          }
        };

        const cancelAudio = () => finish(false);
        this.activeAudioCancel = cancelAudio;

        audio.onended = () => {
          console.log('[SpeechDebug] TTS audio ended', { text: cleanText, time: new Date().toISOString() });
          if (this.currentAudio === audio) this.currentAudio = null;
          finish(true);
        };
        audio.onerror = () => {
          console.log('[SpeechDebug] TTS audio error', {
            text: cleanText,
            playbackStarted,
            time: new Date().toISOString(),
          });
          finish(playbackStarted);
        };

        // Attempt direct play
        audio.play().then(() => {
          playbackStarted = true;
          console.log('[SpeechDebug] TTS audio play resolved', { text: cleanText, time: new Date().toISOString() });
        }).catch((error: unknown) => {
          console.log('[SpeechDebug] TTS audio play rejected', {
            text: cleanText,
            error: error instanceof Error ? error.message : String(error),
            time: new Date().toISOString(),
          });
          finish(false);
        });

        // Safety timeout so Promise never hangs
        setTimeout(() => {
          console.log('[SpeechDebug] TTS audio safety timeout', {
            text: cleanText,
            playbackStarted,
            time: new Date().toISOString(),
          });
          finish(playbackStarted);
        }, 6000);
      });
    } catch (err) {
      console.warn('playAudioTTS failed:', err);
      return false;
    }
  }

  /**
   * Fallback: Uses Web Speech API if server audio stream is inaccessible
   */
  private speakWithWebSpeech(text: string, options: SpeechOptions = {}): Promise<void> {
    if (!this.synth) return Promise.resolve();

    this.synth.cancel();
    if (this.synth.paused) {
      this.synth.resume();
    }

    return new Promise((resolve) => {
      try {
        const utterance = new SpeechSynthesisUtterance(text);
        this.activeUtterance = utterance;

        this.loadVoices();
        if (this.indonesianVoice) {
          utterance.voice = this.indonesianVoice;
          utterance.lang = 'id-ID';
        } else {
          // If no Indonesian voice, avoid setting mismatched id-ID which crashes in Chrome
          const voices = this.synth?.getVoices();
          if (voices && voices.length > 0) {
            utterance.voice = voices[0];
            utterance.lang = voices[0].lang || 'en-US';
          }
        }

        utterance.rate = options.rate ?? 0.85;
        utterance.pitch = options.pitch ?? 1.1;
        utterance.volume = options.volume ?? 1.0;

        let finished = false;
        const done = () => {
          if (!finished) {
            finished = true;
            this.activeUtterance = null;
            resolve();
          }
        };

        utterance.onend = done;
        utterance.onerror = () => done();
        setTimeout(done, 5000);

        this.synth?.speak(utterance);
      } catch (err) {
        console.warn('Web Speech error:', err);
        resolve();
      }
    });
  }

  /**
   * Main Speak Method
   * Speaks any Indonesian text or sentence
   */
  public async speak(text: string, options: SpeechOptions = {}): Promise<void> {
    const cleanText = text.trim();
    if (!cleanText) return;
    if (this.activeSpeechText === cleanText && this.activeSpeechPromise) {
      return this.activeSpeechPromise;
    }

    const requestId = ++this.speechRequestId;
    this.unlockAudio();
    this.stopActiveSpeech();

    const speechPromise = Promise.resolve().then(async () => {
      // First try the crystal-clear native Indonesian TTS stream
      const success = await this.playAudioTTS(cleanText);
      if (requestId !== this.speechRequestId) return;
      if (shouldFallbackToOfflineSpeech(success)) {
        console.log('[SpeechDebug] falling back to Web Speech', {
          text: cleanText,
          requestId,
          time: new Date().toISOString(),
        });
        // Fallback to Web Speech API
        await this.speakWithWebSpeech(cleanText, options);
      }
    });

    this.activeSpeechText = cleanText;
    this.activeSpeechPromise = speechPromise;
    try {
      await speechPromise;
    } finally {
      if (this.activeSpeechPromise === speechPromise) {
        this.activeSpeechText = null;
        this.activeSpeechPromise = null;
      }
    }
  }

  /**
   * Pronounces a letter and its example word narration:
   * e.g. "B! Seperti Bola." or "N! Seperti Nasi."
   */
  public async speakLetter(letter: string, exampleWord?: string): Promise<void> {
    this.playChime('tap');
    const l = letter.toUpperCase();
    if (exampleWord) {
      // Single continuous natural narration
      await this.speak(`${l}! Seperti ${exampleWord}.`, { rate: 0.85 });
    } else {
      await this.speak(l, { rate: 0.85 });
    }
  }

  /**
   * Pronounces a word syllable by syllable, then the complete word:
   * e.g. "ba... ju... baju!"
   */
  public async speakSyllablesAndWord(syllables: string[], word: string): Promise<void> {
    this.playChime('tap');
    const phrase = `${syllables.join('... ')}... ${word}!`;
    await this.speak(phrase, { rate: 0.85 });
  }

  public stop(): Promise<void> {
    this.speechRequestId += 1;
    this.stopActiveSpeech();
    this.stopChime();
    this.activeSpeechText = null;
    this.activeSpeechPromise = null;
    return Promise.resolve();
  }
}

export const speechProvider = new SpeechProvider();
